import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  input,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { interval, lastValueFrom, map, Subscription } from 'rxjs';
// Type-only: the library itself loads on demand in ngOnInit so it stays out of main.js (US 34498).
import type { service } from 'powerbi-client';
import { HttpClient } from '@angular/common/http';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { ExportAccessService } from '@ups/xplat/web/core';
import {
  paginatedReportEmbedUrl,
  spartaReportAccessUrl,
} from './powerbi-report-access.util';

interface ReportAccessVM {
  token: string;
  expiration: string;
  model: Model;
  report: ReportAccess;
  /** Bug 37065: the viewer's key for reports that scope rows by it; absent outside Sparta paginated reports. */
  accessKey?: string | null;
}

interface Model {
  datasets: { id: string }[];
  reports: { id: string }[];
}

interface ReportAccess {
  id: string;
  name: string;
  embedUrl: string;
  datasetId: string;
  datasetWorkpsaceId: null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ups-powerbi-report',
  imports: [CommonModule, DialogModule],
  templateUrl: './powerbi-report.html',
  styleUrl: './powerbi-report.scss',
})
export class PowerBIReportComponent implements OnInit, OnDestroy {
  reportPage = input<string>();
  queryString = input<string>();
  paginatedReportId = input<string>();

  http = inject(HttpClient);
  httpFactory = inject(MyHttpClientFactory);

  softwareSecurityHttpClient: HttpClient;
  spartaHttpClient: HttpClient;
  powerbiService: service.Service;

  refreshDialogOpened = signal(false);
  /** True when the on-demand powerbi-client chunk failed to load (offline, or a deploy replaced it). */
  reportLoadFailed = signal(false);
  refreshTimerSubscription: Subscription | null = null;

  private readonly exportAccess = inject(ExportAccessService);
  private powerbi: typeof import('powerbi-client');
  /** Set on destroy so an in-flight lazy import or access request cannot embed into the next page's #embedContainer. */
  private destroyed = false;

  async ngOnInit() {
    this.spartaHttpClient = this.httpFactory.createHttpClient(
      environment.urls.spartaAPI,
      true,
      ResponseCasingEnum.CamelCase
    );

    this.softwareSecurityHttpClient = this.httpFactory.createHttpClient(
      environment.urls.softwareSecurityAPI,
      true,
      ResponseCasingEnum.CamelCase
    );

    await this.loadAndEmbedReport();
  }

  async retryReportLoad() {
    this.reportLoadFailed.set(false);
    await this.loadAndEmbedReport();
  }

  async embedReport(refresh = false) {
    const res = this.paginatedReportId()
      ? await this.getSpartaReportAccess(this.paginatedReportId(), refresh)
      : await this.getReportAccess();

    if (this.destroyed) {
      return;
    }

    if (this.paginatedReportId()) {
      this.startSessionExpirationCheck();
    }

    // #34476: best effort. Power BI serves the visual "Export data" itself, so no API can
    // reject it; hide the command for export-restricted users. Bug 37809: a PAGINATED
    // report has no exportData command, and powerbi-client validates its commands as an
    // object holding only parameterPanel, so the array makes embed() throw ("commands
    // must be an object") and the report never renders. Send no commands there.
    const exportAllowed = await this.exportAccess.resolved;
    if (this.destroyed) {
      return;
    }
    const hideExportCommand = !exportAllowed && !this.paginatedReportId();

    const embedConfig = {
      type: 'report',
      id: res.report.id,
      embedUrl: paginatedReportEmbedUrl(
        res.report.embedUrl,
        this.queryString(),
        res.accessKey
      ),
      groupId: res.report.datasetWorkpsaceId,
      tokenType: this.powerbi.models.TokenType.Embed,
      accessToken: res.token,
      permissions: this.powerbi.models.Permissions.All,
      settings: {
        filterPaneEnabled: false,
        navContentPaneEnabled: false,
        navBarEnabled: false,
        pageSize: { width: '100%', height: '100%' },
        hideHeaders: true,
        ...(hideExportCommand
          ? {
              commands: [
                {
                  exportData: {
                    displayOption:
                      this.powerbi.models.CommandDisplayOption.Hidden,
                  },
                },
              ],
            }
          : {}),
      },
    };

    const embedContainer = document.getElementById(
      'embedContainer'
    ) as HTMLElement;

    this.powerbiService.embed(embedContainer, embedConfig);
  }

  reembedReport() {
    const embedContainer = document.getElementById(
      'embedContainer'
    ) as HTMLElement;
    this.refreshTimerSubscription.unsubscribe();
    this.powerbiService.reset(embedContainer);
    // Session renewal, not a new open: /refresh does not log a ReportStatistics row (Bug 34319).
    this.embedReport(true);
  }

  closeRefreshDialog() {
    this.refreshDialogOpened.set(false);
    this.reembedReport();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.refreshTimerSubscription?.unsubscribe();
  }

  protected loadPowerBiLibrary(): Promise<typeof import('powerbi-client')> {
    return import('powerbi-client');
  }

  private async loadAndEmbedReport() {
    try {
      this.powerbi = await this.loadPowerBiLibrary();
    } catch {
      // The library used to ship in main.js; loading it on demand adds this failure mode, so show a retry instead of a blank page.
      this.reportLoadFailed.set(!this.destroyed);
      return;
    }

    if (this.destroyed) {
      return;
    }

    const { service: pbiService, factories } = this.powerbi;
    this.powerbiService = new pbiService.Service(
      factories.hpmFactory,
      factories.wpmpFactory,
      factories.routerFactory
    );

    await this.embedReport();
  }

  private startSessionExpirationCheck() {
    this.refreshTimerSubscription = interval(59 * 60 * 1000)
      .pipe(
        map(() => {
          this.refreshDialogOpened.set(true);
        })
      )
      .subscribe();
  }

  private async getReportAccess(): Promise<ReportAccessVM> {
    const url = `/api/PowerBI/reportAccess?name=${this.reportPage()}`;
    const res = (await lastValueFrom(
      this.softwareSecurityHttpClient.get(url)
    )) as ReportAccessVM;

    return res;
  }

  private async getSpartaReportAccess(
    paginatedReportId: string,
    refresh = false
  ): Promise<ReportAccessVM> {
    const url = spartaReportAccessUrl(paginatedReportId, refresh);

    const res = (await lastValueFrom(
      this.spartaHttpClient.get(url)
    )) as ReportAccessVM;

    return res;
  }
}
